import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import log from 'loglevel'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  ConsentStatus as ProtoConsentStatus,
  RecordConsentRequest,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { initDatadogMonitoring } from '../Utils/AppInit'
import { isEuProd } from '../Utils/environment'
import { EnvConfig } from '../config/config'
import { CONSENT_POLICY_VERSION } from '../analytics/analytics.constants'
import { useAuthorizedGrpcClient } from '../adapter'
import { AuthContext } from './authContext'

const logger = log.getLogger('context')

export type ConsentStatus = 'accepted' | 'declined' | null

const storageKey = (): string => `${EnvConfig.name}_trustero_consent`

type StoredConsent = { status: 'accepted' | 'declined'; policyVersion: string }

function readSessionConsent(): ConsentStatus {
  const raw = sessionStorage.getItem(storageKey())
  if (!raw) return null
  try {
    const parsed: StoredConsent = JSON.parse(raw)
    if (parsed.policyVersion !== CONSENT_POLICY_VERSION) return null
    return parsed.status
  } catch {
    return null
  }
}

function writeSessionConsent(status: 'accepted' | 'declined'): void {
  const toStore: StoredConsent = {
    status,
    policyVersion: CONSENT_POLICY_VERSION,
  }
  sessionStorage.setItem(storageKey(), JSON.stringify(toStore))
}

export function clearSessionConsent(): void {
  sessionStorage.removeItem(storageKey())
}

export const ConsentContext = createContext<{
  consentStatus: ConsentStatus
  showBanner: boolean
  accept: () => void
  decline: () => void
  reopenBanner: () => void
}>({
  consentStatus: 'accepted',
  showBanner: false,
  accept: () => undefined,
  decline: () => undefined,
  reopenBanner: () => undefined,
})

export const ConsentProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const isEu = isEuProd()
  const { authCtx } = useContext(AuthContext)
  const AccountClient = useAuthorizedGrpcClient(AccountPromiseClient)

  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() =>
    isEu ? readSessionConsent() : 'accepted',
  )
  const [forceShowBanner, setForceShowBanner] = useState(false)
  // Set synchronously at the start of record() so an in-flight reconcile
  // that resolves afterward never overwrites the user's own click.
  const userDecidedRef = useRef(false)

  // Reset per login/account transition: the guard should only protect against
  // a click landing during *this* login's reconcile (including while its
  // permissions load), not suppress every future login's reconcile forever.
  useEffect(() => {
    userDecidedRef.current = false
  }, [authCtx.isAuthenticated, authCtx.accountId])

  // On login, reconcile with the backend record (authoritative once known).
  useEffect(() => {
    // Wait for login's permissions: they change the session the client is
    // bound to, so a call started before they land is rejected on response.
    if (!isEu || !authCtx.isAuthenticated || authCtx.isAuthLoading) return

    let cancelled = false
    const reconcile = async () => {
      try {
        const resp = await AccountClient.getConsent(new Empty())
        if (cancelled || userDecidedRef.current) return
        if (resp.getHasRecord()) {
          const status =
            resp.getStatus() === ProtoConsentStatus.CONSENT_STATUS_ACCEPTED
              ? 'accepted'
              : 'declined'
          writeSessionConsent(status)
          setConsentStatus(status)
          if (status === 'accepted') {
            initDatadogMonitoring()
          }
          return
        }
        // No backend record yet — if we already have a pre-login decision
        // cached this session, persist it now that we know who they are.
        const local = readSessionConsent()
        if (local) {
          await AccountClient.recordConsent(
            new RecordConsentRequest()
              .setPolicyVersion(CONSENT_POLICY_VERSION)
              .setStatus(
                local === 'accepted'
                  ? ProtoConsentStatus.CONSENT_STATUS_ACCEPTED
                  : ProtoConsentStatus.CONSENT_STATUS_DECLINED,
              ),
          )
        }
      } catch (err) {
        logger.error('failed to reconcile consent with backend', err)
      }
    }
    reconcile()
    return () => {
      cancelled = true
    }
    // AccountClient is memoized on the whole authCtx object (see
    // grpcClient.ts), which gets a new identity on every LOGIN,
    // REFRESH_TOKEN, and LOAD_PERMISSIONS action — not just login/logout.
    // Scope this effect to accountId + isAuthenticated (and the end of
    // login's permission load) so it only re-runs on an actual login/logout
    // transition; AccountClient is still used inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEu, authCtx.isAuthenticated, authCtx.accountId, authCtx.isAuthLoading])

  const record = useCallback(
    (status: 'accepted' | 'declined') => {
      userDecidedRef.current = true
      writeSessionConsent(status)
      setConsentStatus(status)
      setForceShowBanner(false)
      if (status === 'accepted') {
        initDatadogMonitoring()
      }
      if (authCtx.isAuthenticated) {
        AccountClient.recordConsent(
          new RecordConsentRequest()
            .setPolicyVersion(CONSENT_POLICY_VERSION)
            .setStatus(
              status === 'accepted'
                ? ProtoConsentStatus.CONSENT_STATUS_ACCEPTED
                : ProtoConsentStatus.CONSENT_STATUS_DECLINED,
            ),
        ).catch((err: unknown) => logger.error('failed to record consent', err))
      }
    },
    [authCtx.isAuthenticated, AccountClient],
  )

  const accept = useCallback(() => record('accepted'), [record])
  const decline = useCallback(() => record('declined'), [record])
  const reopenBanner = useCallback(() => setForceShowBanner(true), [])

  const showBanner = isEu && (consentStatus === null || forceShowBanner)

  return (
    <ConsentContext.Provider
      value={{ consentStatus, showBanner, accept, decline, reopenBanner }}
    >
      {children}
    </ConsentContext.Provider>
  )
}

export const useConsent = (): {
  consentStatus: ConsentStatus
  showBanner: boolean
  accept: () => void
  decline: () => void
  reopenBanner: () => void
} => useContext(ConsentContext)
